import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Instagram, Menu, MessageCircle, X } from "lucide-react";
import { CONTACTS, waLink } from "@/lib/constants";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/catalogo", label: "Catalogo" },
  { to: "/storia", label: "La nostra storia" },
  { to: "/novita", label: "Novità" },
  { to: "/promo", label: "Promo", badge: "NEW" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="fixed top-0 z-50 w-full border-b border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 md:px-8">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center" onClick={() => setOpen(false)}>
          <img src="/assets/logo-gold.png" alt="Oro Jewels — Gioielli & Orologi" className="h-10 w-auto md:h-12" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 hover:text-gold ${
                  isActive ? "text-gold" : "text-[#A3A3A3]"
                }`
              }
            >
              {l.label}
              {l.badge && (
                <span className="absolute -right-4 -top-2.5 rotate-6 bg-gold px-1 py-px text-[7px] font-bold tracking-widest text-black" data-testid="nav-promo-badge">
                  {l.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={CONTACTS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram-link"
            aria-label="Instagram"
            className="hidden text-[#A3A3A3] transition-colors duration-300 hover:text-gold sm:block"
          >
            <Instagram size={18} />
          </a>
          <a
            href={waLink("Buongiorno, vorrei informazioni sui vostri gioielli.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="hidden items-center gap-2 bg-gold px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-gold-hover sm:flex"
          >
            <MessageCircle size={14} />
            WhatsApp
          </a>
          <button
            data-testid="nav-mobile-menu-button"
            className="text-[#FAFAFA] lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={pathname + "-mobile"}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#0a0a0a]/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l, i) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `border-b border-white/5 py-3 font-serif text-2xl transition-colors ${
                      isActive ? "text-gold" : "text-[#FAFAFA]"
                    }`
                  }
                  style={{ transitionDelay: `${i * 30}ms` }}
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={waLink("Buongiorno, vorrei informazioni sui vostri gioielli.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-whatsapp-cta"
                className="mt-4 flex items-center justify-center gap-2 bg-gold px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-black"
              >
                <MessageCircle size={14} />
                Scrivici su WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
